/**
 * Sentry Session Replay 网络详情 + HTTP 面包屑脱敏
 * @see https://docs.sentry.io/platforms/javascript/session-replay/configuration/#network-details
 */

const SENSITIVE_KEY_RE =
  /password|passwd|token|secret|authorization|cookie|idcard|id_card|mobile|phone|bank|cardno|card_no/i;

const MAX_STRING_LEN = 4000;
const MAX_DEPTH = 5;

export function getReplayIntegrationOptions() {
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : '';

  return {
    maskAllText: false,
    blockAllMedia: false,
    networkCaptureBodies: true,
    networkDetailAllowUrls: [
      origin,
      /^\//,
      /\/zuul\//i,
      /^https?:\/\/([^/]+\.)?liangbaba\.com/i,
    ],
    networkDetailDenyUrls: [
      /agentConfig/i,
      /wxConfig/i,
      /oauth/i,
      /authorize/i,
      /password/i,
      /getSecretKey/i,
    ],
    networkRequestHeaders: ['Content-Type', 'Accept', 'qwSource'],
    networkResponseHeaders: ['Content-Type'],
  };
}

export function getTracePropagationTargets() {
  return [
    'localhost',
    /^https?:\/\/([^/]+\.)?liangbaba\.com/i,
    /^\//,
  ];
}

function truncateString(str) {
  if (str.length <= MAX_STRING_LEN) return str;
  return `${str.slice(0, MAX_STRING_LEN)}…[truncated]`;
}

export function sanitizeForSentry(value, depth = 0) {
  if (value == null) return value;
  if (depth > MAX_DEPTH) return '[Max Depth]';

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return sanitizeForSentry(parsed, depth + 1);
    } catch (e) {
      return truncateString(value);
    }
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeForSentry(item, depth + 1));
  }

  if (typeof value === 'object') {
    const out = {};
    Object.keys(value).forEach((key) => {
      if (SENSITIVE_KEY_RE.test(key)) {
        out[key] = '[Filtered]';
      } else {
        out[key] = sanitizeForSentry(value[key], depth + 1);
      }
    });
    return out;
  }

  return value;
}

export function buildHttpBreadcrumb(config, responseData, options = {}) {
  const { level = 'info', status } = options;
  const apiKey = config._sentryApiKey || config.url || 'unknown';
  const method = (config.method || 'get').toUpperCase();
  const businessCode =
    responseData && typeof responseData === 'object'
      ? responseData.code
      : undefined;

  return {
    category: 'http',
    type: 'http',
    level,
    data: {
      api: apiKey,
      method,
      url: config.url,
      status,
      businessCode,
      requestParams: sanitizeForSentry(config.params),
      requestBody: sanitizeForSentry(config.data),
      responseBody: sanitizeForSentry(responseData),
    },
  };
}

export function shouldReportSentry() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return false;
  const mode = import.meta.env.MODE;
  return mode !== 'development';
}
